import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, Heading, Paragraph } from '@/components/atoms';
import { ClockIcon } from 'lucide-react';
import { cva } from 'class-variance-authority';
import Facebook from '@/assets/svg/facebook.svg?react';
import Instagram from '@/assets/svg/instagram.svg?react';
import Whatsapp from '@/assets/svg/whatsapp.svg?react';
import * as React from 'react';

export interface ConversationItemProps {
  id: string;
  name: string;
  initials: string;
  lastMessage: string;
  time: string;
  channel: 'whatsapp' | 'instagram' | 'facebook';
  status: 'hot' | 'warm' | 'cold';
  unread?: number;
  avatar?: string | null;
  responseWindow?: {
    expiresAt: string | null;
    extendedWindowExpiresAt: string | null;
    isExpired: boolean;
    canSendFreeForm: boolean;
  };
  isSelected?: boolean;
  onClick?: () => void;
  className?: string;
}

const statusBadgeVariants = cva(
  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none',
  {
    variants: {
      status: {
        hot: 'bg-red-500 text-white',
        warm: 'bg-yellow-500 text-white',
        cold: 'bg-blue-500 text-white',
      },
    },
    defaultVariants: {
      status: 'hot',
    },
  }
);

const getChannelIcon = (channel: string) => {
  switch (channel) {
    case 'whatsapp':
      return <Whatsapp className="w-4 h-4" />;
    case 'instagram':
      return <Instagram className="w-4 h-4" />;
    case 'facebook':
      return <Facebook className="w-4 h-4" />;
    default:
      return null;
  }
};

const formatRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

function ConversationItem({
  // id,
  name,
  initials,
  lastMessage,
  time,
  channel,
  status,
  unread = 0,
  avatar,
  responseWindow,
  isSelected = false,
  onClick,
  className,
  ...props
}: ConversationItemProps) {
  const showUnreadBadge = unread > 0;
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!responseWindow?.expiresAt || responseWindow.isExpired) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [responseWindow?.expiresAt, responseWindow?.isExpired]);

  const responseTimer = React.useMemo(() => {
    if (!responseWindow) return null;
    if (!responseWindow.canSendFreeForm || responseWindow.isExpired) {
      return { label: 'Expired', tone: 'muted' as const };
    }
    if (!responseWindow.expiresAt) return null;
    const expiresAtMs = new Date(responseWindow.expiresAt).getTime();
    const remaining = expiresAtMs - now;
    if (remaining <= 0) return { label: 'Expired', tone: 'muted' as const };
    return { label: formatRemaining(remaining), tone: 'active' as const };
  }, [now, responseWindow]);

  return (
    <div
      className={cn(
        'p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50',
        isSelected && 'bg-green-50 border-l-4 border-l-green-500',
        className
      )}
      onClick={onClick}
      {...props}
    >
      <div className="flex items-start gap-3">
        <div className="relative">
          <Avatar className="w-10 h-10">
            {avatar ? (
              <img
                src={avatar}
                alt={name}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <AvatarFallback className="bg-gray-200 text-gray-600">
                {initials}
              </AvatarFallback>
            )}
          </Avatar>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-2">
              <span className={cn(statusBadgeVariants({ status }))}>
                {status === 'hot' ? 'Hot' : status === 'warm' ? 'Warm' : 'Cold'}
              </span>
              <Heading size="sm" className="truncate">
                {name}
              </Heading>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {showUnreadBadge && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
              <Paragraph size="xs" className="text-gray-500">
                {time}
              </Paragraph>
            </div>
          </div>
          <Paragraph size="sm" className="text-gray-600 truncate mt-1">
            {lastMessage}
          </Paragraph>
          <div className="mt-2 flex items-center gap-2">
            {getChannelIcon(channel)}
            <Paragraph size="xs" className="text-gray-500 capitalize">
              {channel}
            </Paragraph>
            {responseTimer && (
              <div
                className={cn(
                  'flex items-center gap-1 text-xs',
                  responseTimer.tone === 'active'
                    ? 'text-green-600'
                    : 'text-red-500'
                )}
                title={
                  responseTimer.label === 'Expired'
                    ? 'Response window expired'
                    : 'Response window remaining'
                }
              >
                <ClockIcon className="w-3.5 h-3.5" />
                <span className="tabular-nums">{responseTimer.label}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { ConversationItem };
